"use client";
import React, { FormEvent } from "react";
import { useRouter } from "next/navigation";

import Button from "@mui/material/Button";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import { getPublicAssetPath, getSafe, getUrl, sha256Hash } from "@/utils";
import getClientText from "@/app/i18n";
import NotificationContext from "@/ContextProviders/NotificationContext/NotificationContextProvider";
import IconMail from "@/components/Icons/icon-mail";
import IconLockDots from "@/components/Icons/icon-lock-dots";
import IconLogin from "@/components/Icons/icon-login";
import LoadingContext from "@/ContextProviders/LoadingContextProvider/LoadingContextProvider";
import { signIn, signOut } from "next-auth/react";
import { requestAuthPost } from "@/utils/requestAPI";
import { gzipdecompress } from "@/utils/gZipUtils";
import AlertDialogContext from "@/ContextProviders/AlertDialogContextProvider/AlertDialogContextProvider";

const PageLoginContent = ({
  lang,
  session,
  ...props
}: {
  lang: string;
  session: any;
}) => {
  const alertContext = React.useContext(AlertDialogContext);
  const notificationContext = React.useContext(NotificationContext);
  const loadingContext = React.useContext(LoadingContext);
  const [emailHelper, setEmailHelper] = React.useState("");
  const [passwordHelper, setPasswordHelper] = React.useState("");
  const [, setDirectAppKey] = React.useState("");
  const directAppKeyRef = React.useRef("");
  const directAppKeySourceRef = React.useRef("");
  const hasHandledThaiDCallback = React.useRef(false);

  const tAuthen = getClientText(lang, "authentication");
  const tError = getClientText(lang, "error");
  const router = useRouter();
  const pendingDirectAppKeyStorageKey = "thaibma-direct-app-key";
  const updateDirectAppKey = (appKeyName: string) => {
    directAppKeyRef.current = appKeyName;
    setDirectAppKey(appKeyName);
  };
  const clearResolvedDirectAppKey = () => {
    directAppKeySourceRef.current = "";
    updateDirectAppKey("");
  };
  const getResolvedDirectAppKey = (appKey: string) => {
    if (!appKey || directAppKeySourceRef.current !== appKey) {
      return "";
    }
    return directAppKeyRef.current;
  };
  const setResolvedDirectAppKey = (appKey: string, appKeyName: string) => {
    directAppKeySourceRef.current = appKey;
    updateDirectAppKey(appKeyName);
  };
  const getUrlAppKey = () => {
    const urlParam = new URLSearchParams(
      new URL(window.location.href as string).search,
    );
    return urlParam.get("appKey") || "";
  };
  const getPendingDirectAppKey = () => {
    return sessionStorage.getItem(pendingDirectAppKeyStorageKey) || "";
  };
  const setPendingDirectAppKey = (appKeyName: string) => {
    if (appKeyName) {
      sessionStorage.setItem(pendingDirectAppKeyStorageKey, appKeyName);
    } else {
      sessionStorage.removeItem(pendingDirectAppKeyStorageKey);
    }
  };
  const resolveDirectAppKey = async (
    appKey: string,
    persist: boolean = false,
  ) => {
    if (!appKey) {
      clearResolvedDirectAppKey();
      if (persist) {
        setPendingDirectAppKey("");
      }
      return "";
    }

    const resolvedAppKeyName = getResolvedDirectAppKey(appKey);
    if (resolvedAppKeyName) {
      if (persist) {
        setPendingDirectAppKey(resolvedAppKeyName);
      }
      return resolvedAppKeyName;
    }

    try {
      const bypassPrefix =
        process.env.NEXT_PUBLIC_APPKEY_BYPASS_STARTWITH ?? "";
      const isBypass = getSafe(
        () => gzipdecompress(appKey).startsWith(bypassPrefix),
        false,
      );
      const byPassAppKey = getSafe(
        () => gzipdecompress(appKey).split(bypassPrefix)[1],
        "",
      );

      let appKeyName = "";
      if (!isBypass) {
        const res: any = await requestAuthPost(
          "appkey/get",
          "secure",
          null,
          JSON.stringify({ appKey: appKey }),
          {},
          false,
        );

        if (res.status === 200) {
          appKeyName = res.data || "";
        } else {
          clearResolvedDirectAppKey();
          if (persist) {
            setPendingDirectAppKey("");
          }
          alertContext.showAlert({
            lang: lang,
            buttonType: "ok",
            titleText: tError["noAppKey"],
            messageText: `[${res.code} - ${res.msg}]\n${tError["alertBackToSEC"]}`,
            yesOkAction: () => {
              router.push(`${process.env.NEXT_PUBLIC_IISSEC_URL}`);
              alertContext.hideAlert();
            },
          });
          // notificationContext.showNotification({
          //   severity: "error",
          //   message: `[${res.code} - ${res.msg}] ${tError["noAppKey"]} ${tError["backToSEC"]}`,
          // });
          return null;
        }
      } else {
        appKeyName = byPassAppKey;
      }

      setResolvedDirectAppKey(appKey, appKeyName);
      if (persist) {
        setPendingDirectAppKey(appKeyName);
      }
      return appKeyName;
    } catch (error) {
      clearResolvedDirectAppKey();
      if (persist) {
        setPendingDirectAppKey("");
      }
      notificationContext.showNotification({
        severity: "error",
        message: `${tError["connectionError"]}`,
      });
      return null;
    }
  };

  const clearErrorQueryParams = () => {
    const url = new URL(window.location.href as string);
    const errorParams = ["error", "error_description", "error_uri"];
    let hasChanges = false;

    errorParams.forEach((param) => {
      if (url.searchParams.has(param)) {
        url.searchParams.delete(param);
        hasChanges = true;
      }
    });

    if (!hasChanges) return;

    const nextSearch = url.searchParams.toString();
    const nextUrl = `${url.pathname}${nextSearch ? `?${nextSearch}` : ""}${
      url.hash
    }`;
    window.history.replaceState({}, "", nextUrl);
  };
  const getSigninUrl = (isForceSignout: boolean = false) => {
    const params = new URLSearchParams(window.location.search);

    if (isForceSignout) {
      params.set("forceSignout", "1");
    } else {
      params.delete("forceSignout");
    }

    return `${process.env.NEXT_PUBLIC_BASEURL}/${
      process.env.NEXT_PUBLIC_PATH_URL
        ? `${process.env.NEXT_PUBLIC_PATH_URL}/`
        : ""
    }${lang}/signin${params.toString() ? `?${params.toString()}` : ""}`;
  };

  const directSSO = (appKey: string, appKeyName: string) => {
    const thaiDDirectRef = sessionStorage.getItem("thaibma-sso-ref");
    setTimeout(() => {
      clearResolvedDirectAppKey();
      sessionStorage.removeItem("thaibma-sso-ref");
      sessionStorage.removeItem("thaibma-thaid-state");
      sessionStorage.removeItem(pendingDirectAppKeyStorageKey);
    }, 2000);
    // router.replace(`/${lang}/signin?${thaiDDirectRef}`);
    const urlRef = getSafe(() => {
      const urlParam = new URLSearchParams(
        new URL(window.location.href as string).search,
      );
      return urlParam.get("ref");
    });
    const referer =
      urlRef && urlRef !== ""
        ? urlRef
        : thaiDDirectRef
          ? thaiDDirectRef.replace("ref=", "")
          : undefined;

    setTimeout(() => {
      loadingContext.showLoading(false);
      if (referer !== "" && referer) {
        switch (appKeyName.toLowerCase()) {
          case "sec": {
            window.location.href = `${process.env.NEXT_PUBLIC_BASEURL}/${
              process.env.NEXT_PUBLIC_PATH_URL
                ? process.env.NEXT_PUBLIC_PATH_URL + "/"
                : ""
            }${lang}/signin/${appKeyName}?ref=${
              referer !== "" && referer
                ? referer
                : process.env.NEXT_PUBLIC_DEFAULT_DIRECT_URL
            }`;
            break;
          }

          default: {
            window.location.href = `${process.env.NEXT_PUBLIC_BASEURL}/${
              process.env.NEXT_PUBLIC_PATH_URL
                ? process.env.NEXT_PUBLIC_PATH_URL + "/"
                : ""
            }${lang}/direct?ref=${
              referer !== "" && referer
                ? referer
                : process.env.NEXT_PUBLIC_DEFAULT_DIRECT_URL
            }`;
          }
        }
      } else {
        loadingContext.showLoading(true);
        notificationContext.showNotification({
          severity: "warning",
          message: tError["refRedirectParamNotFound"],

          //position: { vertical: "top", horizontal: "center" },
        });

        setTimeout(() => {
          router.replace(
            (window.location.href = `${process.env.NEXT_PUBLIC_BASEURL}/${
              process.env.NEXT_PUBLIC_PATH_URL
                ? process.env.NEXT_PUBLIC_PATH_URL + "/"
                : ""
            }${lang}/direct?ref=${process.env.NEXT_PUBLIC_DEFAULT_DIRECT_URL}`),
          );
        }, 2000);
      }
    }, 1000);
  };

  const handleSignInSubmit = async (
    usr: string,
    psw: string,
    credentialType: string,
  ) => {
    notificationContext.hideNotification();
    setEmailHelper("");
    setPasswordHelper("");
    loadingContext.showLoading(true);
    let keepLoading = false;
    clearErrorQueryParams();
    try {
      const appKey = getUrlAppKey();
      const appKeyName =
        credentialType === "ThaiD"
          ? appKey
            ? getPendingDirectAppKey() || null
            : ""
          : await resolveDirectAppKey(appKey);

      if (appKey && !appKeyName) {
        return;
      }
      const resolvedAppKeyName = appKeyName || "";

      let response: any = null;

      switch (credentialType) {
        case "credentials": {
          response = await signIn("credentials", {
            username: usr,
            password: (await sha256Hash(psw).then()).toString(),
            applicationKey: appKeyName,
            redirect: false,
          });
          break;
        }
        case "ThaiD": {
          updateDirectAppKey(resolvedAppKeyName);
          response = await signIn("ThaiD", {
            token: usr,
            exp: psw,
            redirect: false,
          });
          break;
        }
      }

      if (!response?.error) {
        keepLoading = true;
        directSSO(appKey, resolvedAppKeyName);
        return;
      }

      // Unauthorized
      const res = getSafe(() => JSON.parse(response.error as string));
      notificationContext.showNotification({
        severity: "error",
        message: res ? `${res.code} - ${res.msg}` : tError["connectionError"],
      });

      await signOut({ redirect: false });
    } catch (error) {
      notificationContext.showNotification({
        severity: "error",
        message: tError["connectionError"],
      });
      clearErrorQueryParams();
      await signOut({ redirect: false });
    } finally {
      if (!keepLoading) {
        loadingContext.showLoading(false);
      }
    }
  };

  const handleThaiDCallback = async () => {
    loadingContext.showLoading(true);
    const urlParam = new URLSearchParams(
      new URL(window.location.href as string).search,
    );
    const thaiDCode = getSafe(() => {
      return urlParam.get("code");
    });
    const thaiDState = getSafe(() => {
      return urlParam.get("state");
    });
    const thaiDError = getSafe(() => {
      return urlParam.get("error");
    });
    const thaiDErrDesc = getSafe(() => {
      return urlParam.get("error_description");
    });
    const thaiDErrURI = getSafe(() => {
      return urlParam.get("error_uri");
    });

    if (thaiDCode && thaiDState) {
      // Sign In with ThaiD Credential
      console.debug("Thai D Sign In Successfully.", thaiDCode, thaiDState);
      try {
        loadingContext.showLoading(true);
        const res: any = await requestAuthPost(
          "signin/thaid",
          "auth",
          null,
          JSON.stringify({
            authorizationCode: thaiDCode,
          }),
          {},
          false,
        );
        if (res.status === 200) {
          const responseData =
            typeof res.data === "string" ? JSON.parse(res.data) : res.data;
          handleSignInSubmit(
            responseData.token.accessToken,
            responseData.token.exp,
            "ThaiD",
          );
        } else {
          notificationContext.showNotification({
            severity: "error",
            message: `${res.code} - ${res.msg}`,
          });
          clearErrorQueryParams();
        }
      } catch (err: any) {
        notificationContext.showNotification({
          severity: "error",
          message: `${err.code} - ${err.msg || err.message}`,
        });
        clearErrorQueryParams();
      } finally {
        loadingContext.showLoading(false);
      }
      return true;
    }

    if (thaiDError && thaiDErrDesc) {
      notificationContext.showNotification({
        severity: "error",
        message: `${thaiDError} - ${thaiDErrDesc}`,
      });
      clearErrorQueryParams();
      loadingContext.showLoading(false);
      return true;
    } else if (thaiDErrURI) {
      router.push(thaiDErrURI);
      loadingContext.showLoading(false);
      return true;
    }
    return false;
  };

  React.useEffect(() => {
    const run = async () => {
      let didThaiDCallback = false;
      if (!hasHandledThaiDCallback.current) {
        hasHandledThaiDCallback.current = true;
        didThaiDCallback = await handleThaiDCallback();
      }
      const ref = window.location.search;
      const appKey = new URLSearchParams(ref).get("appKey") || "";
      const forceSignout = new URLSearchParams(ref).get("forceSignout") === "1";

      if (!appKey) {
        clearResolvedDirectAppKey();
        setPendingDirectAppKey("");
      }

      // isAppKey
      if (appKey && appKey !== "") {
        //Force Signout
        if (session && !forceSignout) {
          loadingContext.showLoading(true);
          try {
            await requestAuthPost(
              "signOut",
              "auth",
              null,
              JSON.stringify({
                accessToken: session.token.accessToken,
              }),
              {},
              false,
            );
          } catch (error) {
            //console.log("signOut error", error);
          } finally {
            await signOut({ redirect: false });
            loadingContext.showLoading(false);
            window.location.href = getSigninUrl(true);
            return;
          }
        }
      }

      if (session) {
        loadingContext.showLoading(true);
        directSSO(appKey, appKey ? getPendingDirectAppKey() : "");
      } else if (!didThaiDCallback) {
        if (forceSignout) {
          window.history.replaceState({}, "", getSigninUrl(false));
        }
        loadingContext.showLoading(false);
      }
    };
    run();
  }, [session]);

  const handleThaiDSubmit = async (e: any) => {
    loadingContext.showLoading(true);
    const appKey = getUrlAppKey();
    const appKeyName = await resolveDirectAppKey(appKey, true);
    if (appKey && !appKeyName) {
      loadingContext.showLoading(false);
      return;
    }
    const thaiDUUID = `ThaiBMAxThaiD-${Date.now()}-${crypto.randomUUID()}`;
    const referer = getSafe(() => {
      const urlParam = new URLSearchParams(
        new URL(window.location.href as string).search,
      );
      return urlParam;
    });
    // const thaiDRedirect = `${process.env.NEXT_PUBLIC_THAID_CALLBACK}${
    //   referer !== "" && referer ? `?ref=${referer}` : ``
    // }`;

    const thaiDRedirect = `${process.env.NEXT_PUBLIC_THAID_CALLBACK}`;
    const thaiDScope =
      (process.env.NEXT_PUBLIC_THAID_SCOPE || "").split(",").join(" ") || "pid";

    sessionStorage.setItem("thaibma-thaid-state", thaiDUUID);
    sessionStorage.setItem("thaibma-sso-ref", referer);

    const thaiDRequest = `${process.env.NEXT_PUBLIC_THAID_REQUEST_URL}?response_type=code&client_id=${process.env.NEXT_PUBLIC_THAID_CLIENT_ID}&redirect_uri=${thaiDRedirect}&scope=${thaiDScope}&state=${thaiDUUID}`;
    await fetch(thaiDRequest, {
      headers: { "content-type": "application/x-www-form-urlencoded" },
    })
      .then((res: any) => {
        router.replace(thaiDRequest);
        router.refresh();
      })
      .catch((err: any) => {})
      .finally(() => {
        router.replace(thaiDRequest);
        router.refresh();
        loadingContext.showLoading(false);
      });
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    //const formData = new FormData(e.currentTarget);
    const usr = (document.getElementById("email") as HTMLFormElement)!.value;
    const psw = (document.getElementById("psw") as HTMLFormElement)!.value;

    if (usr === "" || psw === "") {
      usr === "" ? setEmailHelper(tError["emptyUser"]) : setEmailHelper("");
      psw === ""
        ? setPasswordHelper(tError["emptyPassword"])
        : setPasswordHelper("");
      notificationContext.showNotification({
        severity: "warning",
        message: tError["emptyUserOrPassword"],
      });

      return;
    }

    handleSignInSubmit(usr, psw, "credentials");
  };

  return (
    <Box
      className="login-image space-y-5 dark:text-white"
      sx={{
        display: "flex",
        flexDirection: "column",
        //alignItems: "center",
      }}
    >
      <div>
        <label className="text-[24px] text-[#065dad]">
          {tAuthen["signin"]}
        </label>
        <label className="text-slate-400">{tAuthen["signinDesc"]}</label>
      </div>

      <Box component="form" noValidate sx={{ mt: 1 }}>
        <div>
          <label htmlFor="email">{tAuthen["email"]}</label>
          <div className="relative text-white-dark">
            <input
              id="email"
              type="email"
              placeholder={tAuthen["enterEmail"]}
              className="form-input ps-10 placeholder:text-white-dark"
            />
            <span className="absolute start-4 top-1/2 -translate-y-1/2">
              <IconMail fill={true} />
            </span>
          </div>
        </div>
        <div className="mt-5">
          <label htmlFor="psw">{tAuthen["password"]}</label>
          <div className="relative text-white-dark">
            <input
              id="psw"
              type="password"
              placeholder={tAuthen["enterPassword"]}
              className="form-input ps-10 placeholder:text-white-dark"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSubmit(e);
                }
              }}
            />
            <span className="absolute start-4 top-1/2 -translate-y-1/2">
              <IconLockDots fill={true} />
            </span>
          </div>
        </div>

        <Button
          //type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, textTransform: "none" }}
          onClick={(e) => {
            handleSubmit(e);
          }}
          //onSubmit={handleSubmit}
        >
          {tAuthen.signin}
        </Button>

        <Grid container className="text-center my-5">
          <Grid item xs>
            {tAuthen.forgotPassword}&nbsp;
            <Link
              href={`${process.env.NEXT_PUBLIC_RESET_PASSWORD_URL}`}
              variant="body2"
              className="link-custom-size"
            >
              {tAuthen.clickHere}
            </Link>
          </Grid>
        </Grid>
        {/* {<Button
          //type="submit"
          fullWidth
          variant="contained"
          sx={{ mb: 2, textTransform: "none" }}
          startIcon={
            <img
              src={getPublicAssetPath("/assets/img/thaiD/ThaiDLogo.png")}
              alt="ThaiD"
              width={25}
              height={25}
              style={{ display: "block" }}
            />
          }
          onClick={(e) => {
            handleThaiDSubmit(e);
          }}
          //onSubmit={handleSubmit}
        >
          {tAuthen.thaiDLogin}
        </Button>} */}
        <div className="relative my-7 text-center md:mb-9">
          <span className="absolute inset-x-0 top-1/2 h-px w-full -translate-y-1/2 bg-white-light dark:bg-white-dark"></span>
          <span className="relative bg-white px-2 font-bold uppercase text-white-dark dark:bg-dark dark:text-white-light">
            {tAuthen.or}
          </span>
        </div>

        <Button
          type="button"
          color="warning"
          fullWidth
          sx={{ textTransform: "none" }}
          variant="contained"
          onClick={(e) => {
            router.push(`${process.env.NEXT_PUBLIC_TEMP_REGISTER_URL}`);
          }}
        >
          {tAuthen.signup}
        </Button>
      </Box>
    </Box>
  );
};

export default PageLoginContent;
